import { useEffect, useState } from "react";
import { useGame } from "../app/GameContext";
import type { AutomationState, FuelId, MaterialId } from "../shared/game-types";
import { ActionButton, Meter, PageHeader, formatNumber, labels } from "../components/GameUi";

const materialIds: MaterialId[] = ["structural", "highTemp", "nuclear", "superconductor", "rare"];
const priorityLabels: Record<AutomationState["energyPriority"][number], string> = {
  lifeSupport: "生命保障",
  research: "科研",
  industry: "工业",
  mining: "采矿",
  defense: "防御",
};

export function ResourcesPage() {
  const { state } = useGame();
  const [priority, setPriority] = useState<AutomationState["energyPriority"]>([]);
  const [autoSurvey, setAutoSurvey] = useState(false);
  const [autoRepairBelow, setAutoRepairBelow] = useState(50);
  const [minimumTradeMargin, setMinimumTradeMargin] = useState(10);
  const [minimums, setMinimums] = useState<AutomationState["materialMinimums"]>({});

  useEffect(() => {
    if (!state) return;
    setPriority([...state.automation.energyPriority]);
    setAutoSurvey(state.automation.autoSurvey);
    setAutoRepairBelow(state.automation.autoRepairBelow);
    setMinimumTradeMargin(state.automation.minimumTradeMargin * 100);
    setMinimums({ ...state.automation.materialMinimums });
  }, [state]);

  if (!state) return null;
  const energy = state.resources.energy;
  const advancedUnlocked = state.player.levels.industry >= 2;
  const developedBodies = Object.values(state.developments).filter((development) => development.level >= 2);
  const bodyName = (bodyId: string) => state.systems.flatMap((system) => system.bodies).find((body) => body.id === bodyId)?.name ?? "未知天体";

  function movePriority(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= priority.length) return;
    setPriority((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  return (
    <div className="page-stack">
      <PageHeader eyebrow="ECONOMY // INVENTORY" title="资源与产能" description="能源与工程材料按星球独立存放；这里先显示全星域合计，具体库存请在星图或下方明细查看。科研点全局共享。" />

      <section className="metric-grid" aria-label="资源容量摘要">
        <article className="metric-card resource-card tone-energy" data-tone="energy"><span>各星球能源合计</span><strong>{formatNumber(energy.available)} / {formatNumber(energy.capacity)}</strong><Meter value={energy.available} max={energy.capacity} label="各星球能源合计" tone="energy" /><small>年产 {signed(energy.productionPerYear)} · 维护 −{formatNumber(energy.maintenancePerYear)} · 不能跨星球直接使用</small></article>
        <article className="metric-card resource-card tone-materials" data-tone="materials"><span>各星球工程材料合计</span><strong>{formatNumber(totalMaterials(state.resources.materials))} / {formatNumber(state.resources.materialCapacity)}</strong><Meter value={totalMaterials(state.resources.materials)} max={state.resources.materialCapacity} label="各星球材料合计" tone="materials" /><small>建设只读取所在地库存；跨星球需运输船</small></article>
        <article className="metric-card resource-card tone-research" data-tone="research"><span>科研点</span><strong>{formatNumber(state.resources.research)} / {formatNumber(state.resources.researchCapacity)}</strong><Meter value={state.resources.research} max={state.resources.researchCapacity} label="科研库存" tone="research" /><small>年产 {signed(state.resources.researchPerYear)}{state.technologies.current ? ` · 当前项目已投入 ${formatNumber(state.technologies.current.invested)}` : " · 当前无研究项目"}</small></article>
      </section>

      <details className="panel resource-details">
        <summary><span><b>能源原料与提纯库存</b><small>查看五类燃料的库存、年产、年耗和用途</small></span><span className="status-chip">{labels.levels.energy} {state.player.levels.energy}级</span></summary>
        <div className="resource-detail-content">
        <div className="table-scroll" tabIndex={0} aria-label="能源表，可横向滚动">
          <table>
            <thead><tr><th>项目</th><th>库存</th><th>年产 / 年耗</th><th>来源与用途</th><th>状态</th></tr></thead>
            <tbody>
              <EnergyRow id="chemicalFuel" name="化学燃料" value={energy.chemicalFuel} energy={energy} use="含碳/冰质资源采集提纯；供能源能力1级发电" />
              <EnergyRow id="fissionFuel" name="裂变燃料" value={energy.fissionFuel} energy={energy} use="岩质与金属矿物提纯；供能源能力2–5级发电" />
              <EnergyRow id="fusionFuel" name="聚变燃料" value={energy.fusionFuel} energy={energy} use="冰体/巨行星同位素采集；供能源能力6–8级发电" />
              <EnergyRow id="antimatter" name="反物质" value={energy.antimatter} energy={energy} use="工业能力9级与能源能力9级解锁粒子设施，将能源转换为反物质并用于发电" />
              <EnergyRow id="propellant" name="推进剂" value={energy.propellant} energy={energy} use="挥发物加工；用于新舰加注、维修补给与任务机动" />
              <tr><td><b>各星球能源合计</b></td><td>{formatNumber(energy.available)}</td><td>{signed(energy.productionPerYear)} / -{formatNumber(energy.maintenancePerYear)}</td><td>仅用于文明总览；实际供能按星球独立结算</td><td>{energy.productionPerYear >= energy.maintenancePerYear ? "总量稳定" : "总量存在缺口"}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="field-hint">采矿站/大气采集站提供原料，轨道加工与工业设施负责提纯；当前能源层级会消耗对应燃料。推进剂在造舰和“维修/补给”时扣除。全部由服务器按天体产能与长期衰减结算。</p>
        </div>
      </details>

      <details className="panel resource-details">
        <summary><span><b>各星球本地库存</b><small>查看能源与五类工程材料实际存放位置</small></span><span className="status-chip">{developedBodies.length} 个节点</span></summary>
        <div className="resource-detail-content">
        <div className="table-scroll" tabIndex={0} aria-label="材料表，可横向滚动">
          <table>
            <thead><tr><th>天体</th><th>能源</th>{materialIds.map((id) => <th key={id}>{labels.materials[id]}</th>)}</tr></thead>
            <tbody>{developedBodies.map((development) => <tr key={development.bodyId}><td><b>{bodyName(development.bodyId)}</b></td><td>{formatNumber(development.stockpile.energy)} / {formatNumber(development.stockpile.energyCapacity)}</td>{materialIds.map((id) => <td key={id}>{formatNumber(development.stockpile.materials[id])}</td>)}</tr>)}</tbody>
          </table>
        </div>
        </div>
      </details>

      {advancedUnlocked ? <details className="panel resource-details">
        <summary><span><b>高级资源调度</b><small>能源短缺保障顺序与挂机自动化规则</small></span><span className="status-chip">已解锁</span></summary>
        <div className="resource-detail-content split-grid">
        <section className="panel" aria-labelledby="priority-heading">
          <div className="section-heading"><div><p className="eyebrow">LOAD SHEDDING</p><h2 id="priority-heading">能源不足时的保障顺序</h2></div></div>
          <p className="muted">从上到下优先保障。资源短缺时，低优先级负载先降功率。</p>
          <ol className="priority-list">
            {priority.map((item, index) => (
              <li key={item}>
                <span><b>{index + 1}</b>{priorityLabels[item]}</span>
                <span>
                  <button className="icon-button" aria-label={`提高${priorityLabels[item]}优先级`} disabled={index === 0} onClick={() => movePriority(index, -1)}>↑</button>
                  <button className="icon-button" aria-label={`降低${priorityLabels[item]}优先级`} disabled={index === priority.length - 1} onClick={() => movePriority(index, 1)}>↓</button>
                </span>
              </li>
            ))}
          </ol>
          <ActionButton className="button primary" action={{ type: "SET_ENERGY_PRIORITY", priority }}>保存优先级</ActionButton>
        </section>

        <section className="panel" aria-labelledby="automation-heading">
          <div className="section-heading"><div><p className="eyebrow">AUTOMATION LIMITS</p><h2 id="automation-heading">挂机自动化</h2></div></div>
          <div className="form-grid">
            <label className="check-row"><input type="checkbox" checked={autoSurvey} onChange={(event) => setAutoSurvey(event.target.checked)} /><span><b>自动安排勘测</b><small>仅使用空闲探测器</small></span></label>
            <label><span className="field-label">舰体低于此值自动维修</span><input type="number" min={1} max={100} value={autoRepairBelow} onChange={(event) => setAutoRepairBelow(event.target.valueAsNumber)} /></label>
            <label><span className="field-label">最低贸易利润率 %</span><input type="number" min={0} max={100} value={minimumTradeMargin} onChange={(event) => setMinimumTradeMargin(event.target.valueAsNumber)} /></label>
            {materialIds.map((id) => <label key={id}><span className="field-label">母星{labels.materials[id]}保留线</span><input type="number" min={0} step={10} value={minimums[id] ?? 0} onChange={(event) => setMinimums((current) => ({ ...current, [id]: event.target.valueAsNumber }))} /></label>)}
          </div>
          <ActionButton className="button primary" action={{ type: "SET_AUTOMATION", automation: { autoSurvey, autoRepairBelow, minimumTradeMargin: minimumTradeMargin / 100, materialMinimums: minimums } }}>保存挂机阈值</ActionButton>
        </section>
        </div>
      </details> : (
        <section className="panel locked-feature" aria-labelledby="resource-unlock-heading">
          <span className="status-chip warning">尚未解锁</span>
          <div><p className="eyebrow">NEXT CAPABILITY</p><h2 id="resource-unlock-heading">工业能力 2级：高级资源调度</h2><p>完成工业能力 2级研究后，才需要设置能源短缺保障顺序和挂机自动化。当前阶段只需关注上方三类库存。</p></div>
        </section>
      )}
    </div>
  );
}

function EnergyRow({ id, name, value, use, energy }: {
  id: FuelId;
  name: string;
  value: number;
  use: string;
  energy: NonNullable<ReturnType<typeof useGame>["state"]>["resources"]["energy"];
}) {
  const production = energy.fuelProductionPerYear[id];
  const consumption = energy.fuelConsumptionPerYear[id];
  return <tr><td><b>{name}</b></td><td>{formatNumber(value)}</td><td>{signed(production)} / -{formatNumber(consumption)}</td><td>{use}</td><td>{production > consumption ? "净增长" : value > 0 ? "可用" : "尚未储备"}</td></tr>;
}

function totalMaterials(materials: Record<MaterialId, number>) {
  return Object.values(materials).reduce((sum, value) => sum + value, 0);
}

function signed(value: number) {
  return `${value >= 0 ? "+" : ""}${formatNumber(value)}`;
}
